import { useState, useCallback } from 'react';

/**
 * Generic hook for handling local modal state management
 * Simplifies the common pattern of [isOpenModal, setIsOpenModal] = useState
 *
 * @template T - The type of modal (e.g., 'create', 'edit', 'delete')
 * @template P - The type of props/data to pass to the modal
 *
 * @returns Object containing modal state and control functions
 */
export function useHandleLocalModal<T extends string = string, P = unknown>() {
  const [modals, setModals] = useState<Record<T, { isOpen: boolean; props?: P }>>(
    {} as Record<T, { isOpen: boolean; props?: P }>
  );

  /**
   * Check if a specific modal is open
   */
  const isOpen = useCallback(
    (modalType: T): boolean => {
      return modals[modalType]?.isOpen ?? false;
    },
    [modals]
  );

  /**
   * Get props for a specific modal
   */
  const getProps = useCallback(
    (modalType: T): P | undefined => {
      return modals[modalType]?.props;
    },
    [modals]
  );

  /**
   * Open a modal with optional props
   */
  const openModal = useCallback((modalType: T, props?: P) => {
    setModals((prev) => ({
      ...prev,
      [modalType]: { isOpen: true, props },
    }));
  }, []);

  /**
   * Close a specific modal
   */
  const closeModal = useCallback((modalType: T) => {
    setModals((prev) => ({
      ...prev,
      [modalType]: { isOpen: false, props: undefined },
    }));
  }, []);

  /**
   * Close all modals
   */
  const closeAllModals = useCallback(() => {
    setModals({} as Record<T, { isOpen: boolean; props?: P }>);
  }, []);

  /**
   * Toggle a modal (open if closed, close if open)
   */
  const toggleModal = useCallback((modalType: T, props?: P) => {
    setModals((prev) => {
      const currentModal = prev[modalType];
      const isCurrentlyOpen = currentModal?.isOpen ?? false;

      return {
        ...prev,
        [modalType]: {
          isOpen: !isCurrentlyOpen,
          props: !isCurrentlyOpen ? props : undefined,
        },
      };
    });
  }, []);

  return {
    // State getters
    isOpen,
    getProps,

    // Actions
    openModal,
    closeModal,
    closeAllModals,
    toggleModal,

    // Direct access to modals state (for advanced usage)
    modals,
  };
}

/**
 * Simplified version for single modal usage
 * Returns the traditional [isOpen, setIsOpen] pattern with additional helpers
 */
export function useSimpleModal<P = unknown>(initialProps?: P) {
  const [isOpen, setIsOpen] = useState(false);
  const [values, setValues] = useState<P | undefined>(initialProps ?? undefined);

  const handleOpenModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleToggleModal = useCallback(() => {
    if (isOpen) {
      handleCloseModal();
    } else {
      handleOpenModal();
    }
  }, [isOpen, handleOpenModal, handleCloseModal]);

  const handleSetValuesModal = useCallback((values: P, isWithCloseModal: boolean = false) => {
    setValues(values);
    if (isWithCloseModal) {
      handleCloseModal();
    }
  }, []);

  return {
    isOpen,
    values,
    handleSetValuesModal,
    handleOpenModal,
    handleCloseModal,
    handleToggleModal,
  };
}
